/**
 * Which fields a form asks for, in which order, and how each one is labelled.
 *
 * The same endpoint and the same cache entry as a grid's column widths -- see
 * `table/field-options.ts` -- holding a different shape, because a form
 * presents a field by asking for it rather than by drawing a column.
 *
 * Pure, so the ordering and the split can be tested against plain arrays.
 */
import type { Field, ViewFilterGroup } from '@/api/types'
import type { Condition } from '../table/condition-row'
import { editorFor } from '../fields/ui/edit'

/** One field's place on one form. */
export interface FormFieldOptions {
  /** Shown instead of the field's name. Empty means the field's own name. */
  name?: string
  description?: string
  /** Whether the field is on the form at all. */
  enabled?: boolean
  required?: boolean
  order?: number
  /** Whether the question is only asked when its conditions hold. */
  show_when_matching_conditions?: boolean
  /** Whether all of them must hold, or any one of them. */
  condition_type?: 'AND' | 'OR'
  conditions?: readonly Condition[]
  condition_groups?: readonly ViewFilterGroup[]
}

/**
 * The order a field has when the form has never been told where to put it.
 *
 * The backend's own default, and it is the largest a `SmallIntegerField` holds
 * rather than a number somebody liked: a field created after the form was
 * built has to come last, and there is no room above the maximum.
 */
export const LAST_ON_FORM = 32767

/**
 * Whether a visitor could fill this field in.
 *
 * One question, asked of the editor registry, because the registry is where
 * the answer already is: a type with no editor is one the server computes, and
 * `editorFor` also refuses a field the *field* has been made read-only -- by a
 * field rule, or by a data sync that owns its values. Both are reasons a form
 * cannot ask for it, and the backend agrees: `active_field_options` filters
 * `field__read_only=False`, so a form that asked anyway would be answered
 * without it.
 */
export function canBeOnForm(field: Field): boolean {
  return editorFor(field) !== null
}

/**
 * Every field of the table, in the order the form puts them in.
 *
 * One order over all of them, enabled or not, which is what makes the two lists
 * two halves of one sequence: a field taken off the form and put back returns
 * to where it was rather than to the end. Ties break on id, the same tie-break
 * the backend's `ordering = ("order", "field_id")` uses -- without it two
 * fields sharing an order would swap places on a refetch.
 */
export function orderFormFields(
  fields: readonly Field[],
  options: Record<string, FormFieldOptions>,
): Field[] {
  const orderOf = (field: Field): number =>
    options[String(field.id)]?.order ?? LAST_ON_FORM

  return [...fields].sort((a, b) => orderOf(a) - orderOf(b) || a.id - b.id)
}

export interface FormFieldSplit {
  /** On the form, in the order it asks for them. */
  on: Field[]
  /** Not on the form, and could be. */
  off: Field[]
  /** Not on the form, and could not be: nobody can fill them in. */
  refused: Field[]
}

/**
 * The three lists the builder draws.
 *
 * `enabled` alone is not enough to put a field on the form. A field can be
 * enabled and then made read-only -- a formula written over a text field, a
 * data sync switched on -- and the backend drops it from the form at that
 * point without changing the stored flag. Drawing it as though visitors could
 * still see it would be showing a form nobody is being sent.
 */
export function splitFormFields(
  fields: readonly Field[],
  options: Record<string, FormFieldOptions>,
): FormFieldSplit {
  const ordered = orderFormFields(fields, options)
  return {
    on: ordered.filter(
      (field) => canBeOnForm(field) && options[String(field.id)]?.enabled === true,
    ),
    off: ordered.filter(
      (field) => canBeOnForm(field) && options[String(field.id)]?.enabled !== true,
    ),
    refused: ordered.filter((field) => !canBeOnForm(field)),
  }
}

/**
 * What the visitor is asked, which is the field's name until somebody writes
 * something else.
 *
 * Stored empty rather than pre-filled with the field's name, which is what
 * makes a renamed field renamed on the form too -- and why this is a fallback
 * rather than a default written once.
 */
export function formLabel(field: Field, options: FormFieldOptions | undefined): string {
  const name = options?.name
  return name === undefined || name === '' ? field.name : name
}

/**
 * The whole table's fields renumbered after the form's questions were dragged
 * into a new order.
 *
 * The fields that are *not* on the form keep their absolute places: the two
 * columns read one order, and moving one question past another says nothing
 * about where a field nobody is being asked for belongs. Appending them
 * instead -- which is what upstream does -- would mean the first reorder
 * quietly swept every unasked field to the bottom, and a field taken off the
 * form would stop coming back to where it was.
 */
export function reorderOnForm(
  ordered: readonly Field[],
  ids: readonly number[],
): number[] {
  const moved = [...ids]
  const asked = new Set(ids)
  return ordered.map((field) =>
    asked.has(field.id) ? (moved.shift() ?? field.id) : field.id,
  )
}
